/**
 * Simplify Dialog
 *
 * Material-UI dialog for configuring geometry simplification parameters.
 * Provides a tolerance slider/input for the Douglas-Peucker algorithm.
 *
 * **Validates: Requirements 51.1**
 */

import React, { useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Slider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import {
  setSimplifySourceLayer,
  setSimplifyTolerance,
  setSimplifyHighQuality,
  closeTool,
} from '../../store/slices/geometryProcessingSlice';

interface SimplifyDialogProps {
  open: boolean;
  onExecute: () => void;
}

const TOLERANCE_MARKS = [
  { value: 0.0001, label: '0.0001' },
  { value: 0.001, label: '0.001' },
  { value: 0.01, label: '0.01' },
  { value: 0.1, label: '0.1' },
  { value: 1, label: '1' },
];

export const SimplifyDialog: React.FC<SimplifyDialogProps> = ({ open, onExecute }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { simplifyTool, isProcessing, error, message } = useSelector(
    (state: RootState) => state.geometryProcessing
  );
  const layers = useSelector((state: RootState) => state.layers.layers);

  const handleClose = useCallback(() => {
    dispatch(closeTool());
  }, [dispatch]);

  const handleSourceLayerChange = useCallback(
    (e: any) => {
      dispatch(setSimplifySourceLayer(e.target.value));
    },
    [dispatch]
  );

  const handleToleranceSliderChange = useCallback(
    (_: Event, value: number | number[]) => {
      dispatch(setSimplifyTolerance(value as number));
    },
    [dispatch]
  );

  const handleToleranceInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);
      if (!isNaN(value) && value > 0) {
        dispatch(setSimplifyTolerance(value));
      }
    },
    [dispatch]
  );

  const handleHighQualityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setSimplifyHighQuality(e.target.checked));
    },
    [dispatch]
  );

  // Get feature count for selected layer
  const selectedLayer = useMemo(
    () => layers.find((l) => l.id === simplifyTool.sourceLayerId),
    [layers, simplifyTool.sourceLayerId]
  );

  const vectorLayers = useMemo(() => layers.filter((l) => l.type === 'vector'), [layers]);

  const canExecute = simplifyTool.sourceLayerId && simplifyTool.tolerance > 0 && !isProcessing;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Simplify Geometries</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Reduce vertex count while preserving general shape using the Douglas-Peucker algorithm.
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="simplify-source-layer-label">Source Layer</InputLabel>
            <Select
              labelId="simplify-source-layer-label"
              value={simplifyTool.sourceLayerId || ''}
              onChange={handleSourceLayerChange}
              label="Source Layer"
            >
              {vectorLayers.map((layer) => (
                <MenuItem key={layer.id} value={layer.id}>
                  {layer.name} ({layer.featureCount || layer.features?.length || 0} features)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedLayer && (
            <Typography variant="body2" color="text.secondary">
              Layer type: {selectedLayer.geometryType || 'Mixed'}
              {selectedLayer.features && ` • ${selectedLayer.features.length} features`}
            </Typography>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Simplification Tolerance
            </Typography>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Higher values produce simpler geometries with fewer vertices. Units are in degrees for
              geographic coordinates.
            </Typography>
            <Box sx={{ px: 1, mt: 1 }}>
              <Slider
                value={simplifyTool.tolerance}
                onChange={handleToleranceSliderChange}
                min={0.00001}
                max={1}
                step={0.00001}
                marks={TOLERANCE_MARKS}
                scale={(x) => x}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => v.toFixed(5)}
              />
            </Box>
            <TextField
              label="Tolerance Value"
              type="number"
              value={simplifyTool.tolerance}
              onChange={handleToleranceInputChange}
              inputProps={{ min: 0.00001, step: 0.0001 }}
              fullWidth
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>

          <FormControlLabel
            control={
              <Switch checked={simplifyTool.highQuality} onChange={handleHighQualityChange} />
            }
            label="High quality (slower but more accurate)"
          />

          {error && <Alert severity="error">{error}</Alert>}
          {message && !error && <Alert severity="success">{message}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          onClick={onExecute}
          variant="contained"
          disabled={!canExecute}
          startIcon={isProcessing ? <CircularProgress size={16} /> : undefined}
        >
          {isProcessing ? 'Processing...' : 'Simplify'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimplifyDialog;
